import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BellIcon, XIcon, MessageSquareIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Conversation, subscribeToUserConversations } from '../services/messagingService';
interface StudentNotificationBellProps {
  darkMode?: boolean;
}
interface DisplayNotification {
  id: string;
  conversationId: string;
  title: string;
  preview: string;
  timestamp: Date;
  link: string;
}
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
};
/**
 * Student bell — mirrors NotificationCenter for the student side.
 * Derives message notifications directly from the student's conversation
 * docs (which the student already has read access to under existing
 * Firestore rules). A conversation contributes a row whenever
 * `unreadForStudent > 0`. Click navigates to /messages and passes
 * `openConversationId` in route state so the inbox auto-opens it;
 * `markConversationOpened` then resets the counter, which removes the
 * row from this list on the next snapshot.
 */
export const StudentNotificationBell: React.FC<StudentNotificationBellProps> = ({
  darkMode = true
}) => {
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // Subscribe to student conversations in real-time
  useEffect(() => {
    if (!currentUser?.uid) {
      setConversations([]);
      return;
    }
    const unsubscribe = subscribeToUserConversations(currentUser.uid, convs => setConversations(convs), 'student');
    return () => unsubscribe();
  }, [currentUser?.uid]);
  const displayNotifications = useMemo<DisplayNotification[]>(() => {
    return conversations.filter(c => {
      // Skip conversations the student has archived on their side.
      if (c.archivedForStudent) return false;
      // Primary signal: per-participant counter.
      const counter = typeof c.unreadForStudent === 'number' ? c.unreadForStudent : 0;
      if (counter > 0) return true;
      // Backward-compat: legacy single boolean. Only treat as unread for
      // the student if the last sender was NOT the student.
      if (c.unread === true && c.lastMessageSenderId && c.lastMessageSenderId !== currentUser?.uid) {
        return true;
      }
      return false;
    }).map(c => {
      const ts = (c.lastMessageTimestamp as any)?.toDate?.() ?? (c.lastMessageTimestamp instanceof Date ? c.lastMessageTimestamp : new Date());
      const count = typeof c.unreadForStudent === 'number' && c.unreadForStudent > 0 ? c.unreadForStudent : 1;
      const otherName = c.teacherName || 'Your teacher';
      const title = count > 1 ? `${count} new messages from ${otherName}` : `New message from ${otherName}`;
      const preview = c.lastMessage || '';
      const truncated = preview.length > 80 ? `${preview.slice(0, 80)}…` : preview;
      return {
        id: `message-${c.id}`,
        conversationId: c.id,
        title,
        preview: truncated || `${count} new message${count !== 1 ? 's' : ''}`,
        timestamp: ts,
        link: '/messages'
      };
    }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 10);
  }, [conversations, currentUser?.uid]);
  const unreadCount = displayNotifications.filter(n => !seenIds.has(n.id)).length;
  const handleBellClick = () => {
    if (!isOpen) {
      // Mark all currently displayed as seen so the badge clears once opened.
      setSeenIds(prev => {
        const next = new Set(prev);
        displayNotifications.forEach(n => next.add(n.id));
        return next;
      });
    }
    setIsOpen(!isOpen);
  };
  const handleNotificationClick = (n: DisplayNotification) => {
    setSeenIds(prev => {
      const next = new Set(prev);
      next.add(n.id);
      return next;
    });
    navigate(n.link, {
      state: {
        openConversationId: n.conversationId
      }
    });
    setIsOpen(false);
  };
  // ESC closes desktop dropdown
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isMobile]);
  // Outside click closes desktop dropdown
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile]);
  const renderEmpty = (size: 'sm' | 'lg') => <div className={`${size === 'lg' ? 'px-4 py-12' : 'px-4 py-6'} text-center`} data-id="element-11298">
      <div className={`rounded-full ${size === 'lg' ? 'p-4' : 'p-3'} mx-auto w-fit mb-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-11299">
        <BellIcon className={`${size === 'lg' ? 'h-8 w-8' : 'h-6 w-6'} text-gray-400`} data-id="element-11300" />
      </div>
      <p className={`${size === 'lg' ? 'text-base' : 'text-sm'} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-11301">
        No new messages
      </p>
    </div>;
  const renderRow = (n: DisplayNotification) => <div key={n.id} onClick={() => handleNotificationClick(n)} className={`px-4 py-3 cursor-pointer border-b ${darkMode ? 'border-navy-700 hover:bg-navy-700' : 'border-gray-200 hover:bg-gray-50'} transition-colors`} data-id="element-11302">
      <div className="flex gap-3" data-id="element-11303">
        <div className={`rounded-full p-2 flex-shrink-0 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-11304">
          <MessageSquareIcon className="h-5 w-5 text-deep-pink" data-id="element-11305" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-11306">
          <div className="flex justify-between items-start gap-2" data-id="element-11307">
            <p className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11308">
              {n.title}
            </p>
            <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11309">
              {formatRelativeTime(n.timestamp)}
            </span>
          </div>
          <p className={`text-sm mt-1 truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-11310">
            {n.preview}
          </p>
        </div>
      </div>
    </div>;
  const renderPanel = () => {
    if (isMobile) {
      return createPortal(<>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setIsOpen(false)} data-id="element-11311" />
          <div className="fixed bottom-0 left-0 right-0 z-[70] animate-slide-up" data-id="element-11312">
            <div className={`rounded-t-2xl shadow-2xl max-h-[70vh] overflow-hidden ${darkMode ? 'bg-navy-800' : 'bg-white'}`} onClick={e => e.stopPropagation()} data-id="element-11313">
              <div className={`px-4 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex justify-between items-center sticky top-0 ${darkMode ? 'bg-navy-800' : 'bg-white'} z-10`} data-id="element-11314">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11315">
                  Notifications
                </h3>
                <button onClick={() => setIsOpen(false)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} aria-label="Close notifications" data-id="element-11316">
                  <XIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-11317" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(70vh-60px)]" data-id="element-11318">
                {displayNotifications.length > 0 ? displayNotifications.map(renderRow) : renderEmpty('lg')}
              </div>
            </div>
          </div>
        </>, document.body);
    }
    return <div ref={dropdownRef} className={`absolute right-0 mt-2 w-80 sm:w-96 rounded-lg shadow-lg py-1 z-50 ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-11319">
        <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-11320">
          <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11321">
            Notifications
          </h3>
        </div>
        <div className="max-h-96 overflow-y-auto" data-id="element-11322">
          {displayNotifications.length > 0 ? displayNotifications.map(renderRow) : renderEmpty('sm')}
        </div>
      </div>;
  };
  return <div className="relative" data-id="element-11323">
      <button onClick={handleBellClick} className="relative p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-deep-pink" aria-label="Notifications" data-id="element-11324">
        <BellIcon className="h-6 w-6 text-white" data-id="element-11325" />
        {unreadCount > 0 && <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center bg-deep-pink" data-id="element-11326">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>}
      </button>
      {isOpen && renderPanel()}
    </div>;
};